import { Button } from '../../../components/form/button';
import { Typography } from '../../../components/typography/typography';
import { LocationSelect } from '../../location/select/location-select';
import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { AddProduct } from '../add-product/add-product';
import { Location } from '../../../clients/quipli-service/location/location';
import {
  CreateCustomProductRequest,
  CycleBillingTerms,
  OrderDocumentType,
  OrderPaymentMethod,
  OrderSource,
  OrderStatus,
  OrderType,
} from '../../../clients/quipli-service/order/order';
import Icon from '../../../components/icons/icon';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { OrderSection } from '../order-section/order-section';
import { CustomerInfo } from './customer-info';
import { DataTable, DataTableColumn, DataTableRow } from '../../../components/table/data-table';
import { ProductImage } from '../../../components/image/product/product-image';
import { AssignUnitSelect } from '../assign-unit-select/assign-unit-select';
import { DateTime, Duration, Interval } from 'luxon';
import { displayHumanDuration, safeRound } from '../../../util/util';
import { EditableField, FieldCounter } from '../../../components/field/field';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import { DateTimePicker } from '../../../components/date-picker/date-time-picker';
import { useNavigate } from 'react-router-dom';
import {
  CustomFeeRequest,
  CustomProductFeeRequest,
  CustomProductLineItem,
  DamageWaiverLineItem,
  DeliveryLineItem,
  FeeLineItem,
  FinancialSummary,
  PickupLineItem,
  ProductFeeLineItem,
  ProductLineItem,
  ProductSecurityDepositLineItem,
  SecurityDepositLineItem,
  TaxLineItem,
} from '../../../clients/quipli-service/financials/financials';
import {
  isOrderCustomInput,
  OrderInputItem,
  OrderInputRental,
} from '../../../clients/quipli-service/order/order-calculation';
import { OrderTotals } from './totals';
import Dinero, { Currency } from 'dinero.js';
import { TextArea } from '../../../components/textarea/textarea';
import { toast } from '../../../util/toasts';
import { OrderPaymentMethodSelector } from '../../payment/payment-method-selector';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { Tooltip } from '../../../components/tooltip/tooltip';
import { TenantContext } from '../../../context/tenant-context';
import { User, UserAddress, UserType } from '../../../clients/quipli-service/user/user';
import { Dictionary, keyBy } from 'lodash';
import { Spinner } from '../../../components/spinner/spinner';
import { ProductTrackingType } from '../../../clients/quipli-service/product/product';
import { ceilToStep } from '../../../util/date-time-util';
import { StickyHeader } from '../../../components/sticky-header/sticky-header';
import { ApproveOrder } from '../status-transitions/approve-order';
import {
  CashPaymentMethod,
  CheckPaymentMethod,
  CustomPaymentMethod,
  OnAccountPaymentMethodRequest,
  PaymentMethodRequest,
  PaymentMethodType,
  StripeCard,
  StripePaymentMethodRequest,
} from '../../../clients/quipli-service/payment/payment';
import { ProductImageV2 } from '../../../components/image/product/product-image-v2';
import usePrevious from '../../../hooks/use-previous';
import { Badge } from '../../../components/badge/badge';
import { CapturePayment } from '../status-transitions/capture-payment';
import { usePaymentApiV2 } from '../../../clients/quipli-service/payment/payment-api-v2';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';
import { OrderCycleBillingTermsForm } from '../cycle-billing-terms/cycle-billing-terms';
import { ShippingType } from '../../../clients/quipli-service/delivery/delivery';
import { OrderDeliverySelection } from './order-delivery-selection';
import { useTaxApiV1 } from '../../../clients/quipli-service/tax/tax-api-v1';
import { TaxBreakdown, TaxCode } from '../../../clients/quipli-service/tax/tax';
import { OnAccount } from '../../../clients/quipli-service/payment/on-account/on-account';
import { AssignSalesRep } from '../view/order-view';
import { Configuration } from '../../../clients/quipli-service/configuration/configuration';
import { PaymentContextProvider } from '../../../context/payment-context';
import { useAdmin } from '../../../hooks/use-admin-context';
import { Constants } from '../../../constants/constants';
import { Representative } from '../../../clients/quipli-service/representatives/representative';
import { CustomerSelect } from '../../customer/components/customer-select';

type SelectedUnits = { productId: number; productLineItemId: string; units: number[] };
type DeliveryDescription = { productId: number; text: string };

export const CreateQuote = () => {
  return <CreateOrder title="Create Quote" isQuote />;
};

export const CreateMissedRental = () => {
  return <CreateOrder title="Create Missed Rental" isMissedRental />;
};

export const CreateCreditMemo = () => {
  return <CreateOrder title="Create Credit Memo" isCreditMemo />;
};

export const CreateOrder = ({
  title = 'Create Order',
  isQuote = false,
  isMissedRental = false,
  isCreditMemo = false,
}: {
  title?: string;
  isQuote?: boolean;
  isMissedRental?: boolean;
  isCreditMemo?: boolean;
}) => {
  const initialCycleBillingTermsState = {
    cycleLength: Duration.fromObject({ days: 28 }),
    paymentPeriod: Duration.fromObject({ days: 30 }),
    cycleInvoiceDay: 28,
    isDayOfMonth: false,
  };

  const { calculateFinancialSummary, createOrder, getMultipleConfigurations, canSubmit, updateOrderPayment } = useAPI();
  const { searchTaxCodes, listTaxLocations } = useTaxApiV1();

  const { autoApprove } = useOrderApiV1();

  const { captureCard } = usePaymentApiV2();

  const { hasPermissions } = useAdmin();

  const { contextPath } = useContext(TenantContext);

  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [location, setLocation] = useState<Location | null>(null);
  const [customer, setCustomer] = useState<User | null>(null);
  const [taxRates, setTaxRates] = useState<TaxCode[]>();
  const [taxBreakdown, setTaxBreakdown] = useState<TaxBreakdown[]>();
  const [representative, setRepresentative] = useState<Representative>();
  const [configs, setConfigs] = useState<Dictionary<Configuration>>({});
  const [isCycleBilled, setIsCycleBilled] = useState<boolean>(false);
  const [isAutoCharged, setIsAutoCharged] = useState<boolean>(false);
  const [totalExceedsLimit, setTotalExceedsLimit] = useState<boolean>(false);
  const [isEditingCycleBillingTerms, setIsEditingCycleBillingTerms] = useState<boolean>(false);
  const [cycleBillingTerms, setCycleBillingTerms] = useState<{
    global: CycleBillingTerms;
    order: CycleBillingTerms;
    state: CycleBillingTerms;
  }>({
    global: initialCycleBillingTermsState,
    order: initialCycleBillingTermsState,
    state: initialCycleBillingTermsState,
  });
  const [billingAddress, setBillingAddress] = useState<UserAddress>();
  const [deliveryAddress, setDeliveryAddress] = useState<UserAddress>();
  const [coupon, setCoupon] = useState<string>('-NA-');
  const [paymentType, setPaymentType] = useState<OrderPaymentMethod>();
  const [notes, setNotes] = useState<string>('');
  const [cardId, setCardId] = useState<number>();
  const [card, setCard] = useState<StripeCard>();
  const [customPaymentMethod, setCustomPaymentMethod] = useState<string>();
  const [bankAccountId, setBankAccountId] = useState<string>();
  const [poNumber, setPoNumber] = useState<string>('');

  const [defaultTaxCode, setDefaultTaxCode] = useState<string>();
  const [orderInputs, setOrderInputs] = useState<OrderInputItem[]>([]);
  const [selectedUnits, setSelectedUnits] = useState<SelectedUnits[]>([]);
  const [summary, setSummary] = useState<FinancialSummary>();
  const [customDelivery, setCustomDelivery] = useState<Dinero.Dinero>();
  const [customPickup, setCustomPickup] = useState<Dinero.Dinero>();
  const [customTax, setCustomTax] = useState<Dinero.Dinero>();
  const [customTaxPercentage, setCustomTaxPercentage] = useState<number>();
  const [customDamageWaiver, setCustomDamageWaiver] = useState<Dinero.Dinero>();
  const [customFees, setCustomFees] = useState<CustomFeeRequest[]>([]);
  const [customProductFees, setCustomProductFees] = useState<CustomProductFeeRequest[]>([]);
  const [additionalDiscount, setAdditionalDiscount] = useState<Dinero.Dinero>();
  const [orderId, setOrderId] = useState<number>();
  const [showApproveModal, setShowApproveModal] = useState<boolean>(false);
  const [showCaptureModal, setShowCaptureModal] = useState<boolean>(false);
  const [balance, setBalance] = useState<Dinero.Dinero>();
  const [taxRate, setTaxRate] = useState<{ id?: string; taxCodeSource?: string } | undefined>();
  const [selectedDelivery, setSelectedDelivery] = useState<ShippingType | null>(null);
  const [deliveryDescriptions, setDeliveryDescriptions] = useState<DeliveryDescription[]>([]);
  const [onAccount, setOnAccount] = useState<OnAccount>();
  const [salesRep, setSalesRep] = useState<number>();

  // Quote expiration
  const [quoteExpiration, setQuoteExpiration] = useState<DateTime>();

  const orderPreconditions = () => {
    if (!customer) {
      toast('Please select a customer.', 'error');
      return false;
    }

    if (!location) {
      toast('Please select a location.', 'error');
      return false;
    }

    if (!summary) {
      toast('Please add products.', 'error');
      return false;
    }

    if (!selectedDelivery) {
      toast('Please select a delivery option.', 'error');
      return false;
    }

    if (isCreditMemo && selectedDelivery !== ShippingType.SelfPickUp && !deliveryAddress) {
      toast('Please select a delivery address.', 'error');
      return false;
    }

    if (paymentType === OrderPaymentMethod.OnAccount && !onAccount) {
      toast('Please set up an on account payment method.', 'error');
      return false;
    }

    return true;
  };

  const evaluateSubmission = () => {
    if (!orderPreconditions()) {
      return;
    }
    const cartTotal = summary!.lineItems.reduce(
      (acc, line) => (line.type === 'Product' ? acc.add(line.amount) : acc),
      Dinero({ amount: 0, currency: location!.currency as Currency }),
    );
    if (
      customer?.creditLimit &&
      !customer.creditLimit.isZero() &&
      !isCreditMemo &&
      cartTotal
        .add(balance ?? Dinero({ amount: 0, currency: location?.currency as Currency }))
        .greaterThan(customer.creditLimit)
    ) {
      setTotalExceedsLimit(true);
      !hasPermissions(Constants.Permissions.Credit_Limit_Adjustment) &&
        toast('Order total exceeds credit limit.', 'error');
      return;
    } else {
      submit();
    }
  };

  const submit = async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      if (!customer) {
        toast('Please select a customer.', 'error');
        return;
      }

      if (!location) {
        toast('Please select a location.', 'error');
        return;
      }

      if (!summary) {
        toast('Please add products.', 'error');
        return;
      }

      if (!selectedDelivery) {
        toast('Please select a delivery option.', 'error');
        return;
      }

      if (isCreditMemo && selectedDelivery !== ShippingType.SelfPickUp && !deliveryAddress) {
        toast('Please select a delivery address.', 'error');
        return;
      }

      if (
        !isQuote &&
        !isMissedRental &&
        !isCreditMemo &&
        paymentType === OrderPaymentMethod.CreditCard &&
        !billingAddress
      ) {
        toast('Please select a billing address.', 'error');
        return;
      }

      if (!paymentType && !isQuote && !isMissedRental && !isCreditMemo) {
        toast('Please select a payment method.', 'error');
        return;
      }

      if (!isQuote && !isMissedRental && !isCreditMemo && customer.requirePoNum && poNumber === '') {
        toast('Please enter a PO number for the payment.', 'error');
        return false;
      }

      if (customer.requireCompanyRep && !representative) {
        toast('Please select a company representative.', 'error');
        return false;
      }

      // Check if order can be submitted (only for orders, not quotes)
      if (!isQuote && !isMissedRental && !isCreditMemo && paymentType) {
        const { isValid, errors } = await canSubmit(customer.id, paymentType, cardId, false);
        if (!isValid) {
          errors.forEach((error) => toast(error, 'error'));
          return;
        }
      }

      const cartTotal = summary.lineItems.reduce(
        (acc, line) => (line.type === 'Product' || line.type === 'CustomProduct' ? acc.add(line.amount) : acc),
        Dinero({ amount: 0, currency: location.currency as Currency }),
      );

      const orderDelivery = summary?.lineItems.find((line) => line.type === 'Delivery') as DeliveryLineItem;
      const delivery = orderDelivery
        ? orderDelivery.amount
        : summary?.lineItems.reduce(
            (acc, line) => (line.type === 'ProductDelivery' ? acc.add(line.amount) : acc),
            Dinero({ amount: 0, currency: location.currency as Currency }),
          );
      const orderPickup = summary?.lineItems.find((line) => line.type === 'Pickup') as PickupLineItem;
      const pickup = orderPickup
        ? orderPickup.amount
        : summary?.lineItems.reduce(
            (acc, line) => (line.type === 'ProductPickup' ? acc.add(line.amount) : acc),
            Dinero({ amount: 0, currency: location.currency as Currency }),
          );
      const damageWaiver = summary?.lineItems.find((line) => line.type === 'DamageWaiver') as DamageWaiverLineItem;
      const couponDiscount =
        summary?.lineItems.find((line) => line.type === 'TagPricing' || line.type === 'Coupon')?.amount ||
        Dinero({ amount: 0, currency: location.currency as Currency });
      const tax = summary?.lineItems.find((line) => line.type === 'Tax') as TaxLineItem;
      const locationFees = (summary?.lineItems.filter((line) => line.type === 'Fee') || []) as FeeLineItem[];
      const discount =
        summary?.lineItems.find((line) => line.type === 'AdditionalDiscount')?.amount ??
        Dinero({ amount: 0, currency: location.currency as Currency });

      const orderSecurityDeposit = summary?.lineItems.find((line) => line.type === 'SecurityDeposit') as
        | SecurityDepositLineItem
        | undefined;

      const securityDepositPrice = orderSecurityDeposit
        ? orderSecurityDeposit.amount
        : summary?.lineItems.reduce(
            (acc, line) => (line.type === 'ProductSecurityDeposit' ? acc.add(line.amount) : acc),
            Dinero({ amount: 0, currency: location.currency as Currency }),
          );

      let paymentMethod: PaymentMethodRequest;

      if (isQuote || isMissedRental) {
        paymentMethod = { type: PaymentMethodType.None };
      } else if (isCreditMemo) {
        paymentMethod = { type: PaymentMethodType.Credit };
      } else {
        switch (paymentType) {
          case OrderPaymentMethod.BankAccount:
            paymentMethod = { type: PaymentMethodType.Stripe, bankAccountId, poNumber } as StripePaymentMethodRequest;
            break;
          case OrderPaymentMethod.CreditCard:
            paymentMethod = { type: PaymentMethodType.Stripe, cardId, poNumber } as StripePaymentMethodRequest;
            break;
          case OrderPaymentMethod.Terminal:
            paymentMethod = { type: PaymentMethodType.Stripe, cardId: -1, poNumber } as StripePaymentMethodRequest;
            break;
          case OrderPaymentMethod.OnAccount:
            paymentMethod = {
              type: PaymentMethodType.OnAccount,
              accountNumber: '',
              poNumber,
            } as OnAccountPaymentMethodRequest;
            break;
          case OrderPaymentMethod.Cash:
            paymentMethod = { type: PaymentMethodType.Cash, poNumber } as CashPaymentMethod;
            break;
          case OrderPaymentMethod.Check:
            paymentMethod = { type: PaymentMethodType.Check, poNumber } as CheckPaymentMethod;
            break;
          case OrderPaymentMethod.Custom:
            paymentMethod = {
              type: PaymentMethodType.Custom,
              name: customPaymentMethod,
              poNumber,
            } as CustomPaymentMethod;
            break;
          case OrderPaymentMethod.ProceedWithoutPayment:
          default:
            paymentMethod = { type: PaymentMethodType.None, poNumber };
            break;
        }
      }
      const order = await createOrder({
        customerId: customer.id,
        locationId: location.id,
        billingAddressId: billingAddress?.id,
        deliveryAddressId: deliveryAddress?.id,
        deliveryType: selectedDelivery,
        source: isQuote ? OrderSource.Quote : OrderSource.AdminPhone,
        status: isQuote
          ? OrderStatus.Quote
          : isMissedRental
          ? OrderStatus.Missed
          : isCreditMemo
          ? OrderStatus.Approved
          : OrderStatus.Requested,
        ...(isQuote && { expirationDate: quoteExpiration }),
        notes,
        paymentMethod,
        checkoutBeginDate: DateTime.local({ zone: location.timezone }),
        currency: summary.total.getCurrency(),
        isCycleBilled,
        isAutoCharged,
        cartTotal: cartTotal.getAmount(),
        damageWaiver: damageWaiver
          ? {
              amount: damageWaiver.amount.getAmount(),
              hasCustomAmount: damageWaiver.hasCustomAmount,
            }
          : undefined,
        pickup: pickup
          ? {
              amount: pickup?.getAmount() || 0,
              hasCustomAmount: orderPickup?.hasCustomAmount || false,
            }
          : undefined,
        delivery: delivery
          ? {
              amount: delivery?.getAmount() || 0,
              hasCustomAmount: orderDelivery?.hasCustomAmount || false,
            }
          : undefined,
        tax: {
          amount: tax.amount.getAmount(),
          hasCustomAmount: tax.hasCustomAmount,
          hasCustomPercentage: tax.hasCustomPercentage,
          percentage: tax.percentage,
          taxCode: taxRate?.id !== 'taxJar' ? taxRate?.id : undefined,
          breakdown: tax.taxBreakdown,
        },
        coupon: !couponDiscount.isZero()
          ? {
              code: customer?.tagPricing ? customer.tagPricing.name : coupon,
              amount: couponDiscount.multiply(-1).getAmount(),
              isVip: false,
            }
          : undefined,
        additionalFees: locationFees.map((fee) => ({
          feeId: fee.feeId,
          amount: fee.amount.getAmount(),
          hasCustomAmount: fee.hasCustomAmount,
        })),
        additionalDiscount: !discount.isZero() ? discount.multiply(-1).getAmount() : undefined,
        total: summary.total.getAmount(),
        googleCalculation: summary.googleCalculation,
        securityDeposit: !securityDepositPrice.isZero()
          ? {
              amount: securityDepositPrice.getAmount(),
              percentage: orderSecurityDeposit?.percentage || 0,
            }
          : undefined,
        products: orderInputs.map((input) => {
          if (isOrderCustomInput(input)) {
            const customLineItem = summary.lineItems.find(
              (line) => line.type === 'CustomProduct' && line.id === input.id,
            )! as CustomProductLineItem;

            return {
              uniqueId: input.id,
              productId: input.productId,
              name: input.name,
              description: input.description,
              taxable: input.taxable,
              generalLedgerAccountId: input.generalLedgerAccountId,
              taxCategoryId: input.taxCategoryId,
              type: input.type,
              amount: customLineItem.unitPrice.getAmount(),
              hasCustomAmount: true,
              quantity: input.quantity,
              isCustom: true,
              start: input.type == OrderType.Rental ? input.start : undefined,
              end: input.type == OrderType.Rental && input.end ? input.end : undefined,
              customOrderRentalRates: input.type == OrderType.Rental ? input.customOrderRentalRates : undefined,
              equipmentOwner: input.equipmentOwner,
            } as CreateCustomProductRequest;
          }

          const productLineItem = summary.lineItems.find(
            (line) => line.type === 'Product' && line.id === input.id,
          )! as ProductLineItem;

          return {
            uniqueId: input.id,
            productId: input.productId,
            type: input.type,
            amount: productLineItem.unitPrice.getAmount(),
            hasCustomAmount: productLineItem.hasCustomAmount,
            pickup:
              summary.lineItems
                .find((line) => line.type === 'ProductPickup' && line.productLineItemId === input.id)
                ?.amount?.getAmount() || 0,
            delivery:
              summary.lineItems
                .find((line) => line.type === 'ProductDelivery' && line.productLineItemId === input.id)
                ?.amount?.getAmount() || 0,
            securityDeposit: summary.lineItems
              .find((line) => line.type === 'ProductSecurityDeposit' && line.productLineItemId === input.id)
              ?.amount?.getAmount(),
            quantity: input.quantity,
            additionalFees: summary.lineItems
              .filter((line) => line.type === 'ProductFee' && line.productLineItemId === input.id)
              .map((line) => {
                const fee = line as ProductFeeLineItem;
                return {
                  feeId: fee.productFeeId,
                  amount: fee.amount.getAmount(),
                  hasCustomAmount: fee.hasCustomAmount,
                };
              }),
            discount: input.manualDiscount,
            assignedUnits: selectedUnits.find((u) => u.productLineItemId === input.id)?.units || [],
            start: input.type == OrderType.Rental ? input.start : undefined,
            end: input.type == OrderType.Rental && input.end ? input.end : undefined,
            customOrderRentalRates: input.type == OrderType.Rental ? input.customOrderRentalRates : undefined,
            options: [],
            services: [],
            isCustom: false,
          };
        }),
        cycleBillingTerms: cycleBillingTerms?.order,
        authorizedRepresentative: representative?.id,
        salesRepId: salesRep,
        documentType: isCreditMemo ? OrderDocumentType.CreditMemo : OrderDocumentType.Order,
      });

      if (
        order.status === OrderStatus.Requested &&
        order.products.some((p) => p.type === OrderType.Sale) &&
        order.paymentMethod.type !== PaymentMethodType.Stripe
      ) {
        setShowApproveModal(true);
        setOrderId(order.id);
      } else if (
        order.status !== OrderStatus.Missed &&
        (card?.confirmationTokenId || paymentType == OrderPaymentMethod.Terminal)
      ) {
        if (order.isCycleBilled && card?.confirmationTokenId) {
          try {
            await createOrderCard(customer!.id, card.confirmationTokenId!, order.id);
          } catch (e) {
            //skip
          } finally {
            navigate(`${contextPath}manager/orders/view/${order.id}`);
          }
        } else {
          setShowCaptureModal(true);
          setOrderId(order.id);
        }
      } else {
        navigate(`${contextPath}manager/orders/view/${order.id}`);
      }
    } catch (error: any) {
      toast(typeof error.message === 'string' ? error.message : 'Something went wrong.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const createOrderCard = async (userId: number, confirmationTokenId: string, orderId: number) => {
    try {
      const card = await captureCard({ confirmationTokenId, userId });
      await updateOrderPayment(orderId, { cardId: card.id, type: PaymentMethodType.Stripe });
      toast('Payment updated successfully.');
      navigate(`${contextPath}manager/orders/view/${orderId}`);
    } catch (ex: any) {
      toast(typeof ex.message === 'string' ? ex.message : 'Something went wrong.', 'error');
    }
  };

  useEffect(() => {
    if (!location?.id) return;

    if (!additionalDiscount) {
      setAdditionalDiscount(Dinero({ amount: 0, currency: location.currency as Currency }));
    }

    if (orderInputs.length === 0) {
      setSummary(undefined);
      return;
    }

    const calculate = async () => {
      try {
        const response = await calculateFinancialSummary({
          customerId: customer?.id,
          locationId: location.id,
          billingAddressId: billingAddress?.id,
          deliveryAddressId: deliveryAddress?.id,
          items: isCycleBilled
            ? orderInputs.map((oi) => {
                if (oi.type === OrderType.Rental) {
                  return {
                    ...oi,
                    end: oi.start.plus(cycleBillingTerms!.order.cycleLength),
                  };
                } else {
                  return oi;
                }
              })
            : orderInputs,
          deliveryType: selectedDelivery || undefined,
          couponCode: coupon,
          customDelivery: customDelivery?.getAmount(),
          customPickup: customPickup?.getAmount(),
          customTax: customTax?.getAmount(),
          customTaxPercentage: customTaxPercentage,
          customDamageWaiver: customDamageWaiver?.getAmount(),
          customFees: customFees.length
            ? customFees.map((fee) => ({ feeId: fee.feeId, amount: fee.amount }))
            : undefined,
          customProductFees: customProductFees.length
            ? customProductFees.map((fee) => ({
                feeId: fee.feeId,
                productId: fee.productId,
                productLineItemId: fee.productLineItemId,
                amount: fee.amount,
              }))
            : undefined,
          additionalDiscount: additionalDiscount?.getAmount(),
          paymentMethod: paymentType == OrderPaymentMethod.Terminal ? OrderPaymentMethod.CreditCard : paymentType,
          isCycleBilled,
          cycleLength: cycleBillingTerms?.order.cycleLength,
          taxRateId: taxRate?.id,
          documentType: isCreditMemo ? OrderDocumentType.CreditMemo : OrderDocumentType.Order,
        });

        setSummary(response);
        const taxLineItem = response.lineItems.find((_) => _.type === 'Tax') as TaxLineItem | undefined;
        if (taxLineItem) {
          const taxCode = taxLineItem.taxCodeId;
          const taxCodeSource = taxLineItem.taxCodeSource;
          if (taxCode && taxCode !== taxRate?.id) {
            setTaxRate({ id: taxCode, taxCodeSource });
          }
        }
      } catch (err: any) {
        const errMsg = err.status === 400 && err.message ? err.message : 'Error calculating financial summary';

        toast(errMsg, 'error');
      }
    };

    calculate();
  }, [
    location?.id,
    customer,
    billingAddress,
    deliveryAddress,
    orderInputs,
    selectedDelivery,
    customDelivery,
    customPickup,
    customTax,
    customTaxPercentage,
    customDamageWaiver,
    customFees,
    customProductFees,
    additionalDiscount,
    coupon,
    paymentType,
    taxRate,
  ]);

  useEffect(() => {
    const getLocationTax = async () => {
      const taxLocations = await listTaxLocations({});
      // one default per location, shipping types are not enabled
      const locationTax = taxLocations[0];
      if (locationTax) {
        setDefaultTaxCode(locationTax.taxCodeId);
      }
    };
    if (location?.id && configs['CONF_SITE_TAX_OPTIONS']?.value === '5') {
      getLocationTax();
    }
  }, [location?.id]);

  useEffect(() => {
    const getConfigurations = async () => {
      const configs = await getMultipleConfigurations([
        'CONF_CYCLE_BILLING',
        'CONF_AUTO_CHARGE_ORDER',
        'CONF_CYCLE_BILLING_LENGTH',
        'CONF_CYCLE_IS_DAY_OF_MONTH',
        'CONF_PAYMENT_PERIOD',
        'CONF_CYCLE_INVOICE_DAY',
        'CONF_CREDIT_LIMIT',
        'CONF_SITE_TAX_OPTIONS',
        'CONF_SALE_SERIAL_TRACKING',
        'CONF_COMMISSION_ENABLED',
        'CONF_DEFAULT_CYCLE_BILLING_ON_ADMIN_ORDERS',
      ]);
      const _configs = keyBy(configs, 'variable');
      setConfigs(_configs);
      const globalCbt = {
        cycleLength: Duration.fromISO(_configs['CONF_CYCLE_BILLING_LENGTH']?.value ?? 'P28D'),
        paymentPeriod: Duration.fromISO(_configs['CONF_PAYMENT_PERIOD']?.value ?? 'P30D'),
        cycleInvoiceDay: parseInt(_configs['CONF_CYCLE_INVOICE_DAY']?.value ?? '28'),
        isDayOfMonth: _configs['CONF_CYCLE_IS_DAY_OF_MONTH']?.value === '1',
      };
      setCycleBillingTerms({
        global: globalCbt,
        order: globalCbt,
        state: globalCbt,
      });
      if (_configs['CONF_SITE_TAX_OPTIONS']?.value === '5') {
        const taxRates = await searchTaxCodes({ pageSize: 500 });
        setTaxRates(taxRates.items);
      }
      if (
        _configs['CONF_CYCLE_BILLING']?.value === '1' &&
        _configs['CONF_DEFAULT_CYCLE_BILLING_ON_ADMIN_ORDERS']?.value === '1' &&
        !isCreditMemo
      ) {
        setIsCycleBilled(true);
      }
    };

    getConfigurations();
  }, []);

  useEffect(() => {
    if (customer?.id) {
      setSalesRep(customer.defaultSalesRepId);
      customer.defaultTaxCode &&
        setTaxRate({
          id: customer.defaultTaxCode,
        });
    }
  }, [customer]);

  return (
    <div className="px-[10px]">
      <StickyHeader title={title}>
        <Button
          className="button-yellow"
          onClick={configs['CONF_CREDIT_LIMIT']?.value === '1' ? evaluateSubmission : submit}
          disabled={isSubmitting}
          dataCy="create-order"
        >
          Create {isQuote ? 'quote' : isCreditMemo ? 'credit memo' : 'order'}
        </Button>
      </StickyHeader>
      {/* Components */}
      <div className={classNames('grid grid-cols-1 lg:grid-cols-3 gap-y-[15px] lg:gap-y-0 lg:gap-x-[15px] mt-[15px]')}>
        <div className="col-span-2 order-2 lg:order-1 flex flex-col gap-y-[15px]">
          {/* Location */}
          <OrderSection className="flex flex-col gap-[12px]" header="Rental Location">
            <LocationSelect setLocation={setLocation} dataCy="location-select" />
          </OrderSection>

          {/* Quote expiration */}
          {isQuote && (
            <OrderSection>
              <div className="flex flex-col gap-[12px]">
                <Typography dataCy="quote-expiration" type="text-md" className="text-grey-700">
                  Quote expiration date
                </Typography>

                <div className="w-fit">
                  <DateTimePicker
                    value={quoteExpiration}
                    onChange={setQuoteExpiration}
                    disabledIntervals={[Interval.before(DateTime.now(), { years: 10 })]}
                    dataCy="quote-expiration-date"
                  />
                </div>

                {!!quoteExpiration && (
                  <Typography type="text-sm" className="text-grey-500" dataCy="quote-expiration-label">
                    Quote will expire{' '}
                    {displayHumanDuration(
                      quoteExpiration.diff(ceilToStep(DateTime.now(), Duration.fromObject({ minutes: 30 }))),
                    )}{' '}
                    from now
                  </Typography>
                )}
              </div>
            </OrderSection>
          )}

          {/* Cycle Billing */}
          {configs['CONF_CYCLE_BILLING']?.value === '1' && !isCreditMemo && (
            <OrderSection>
              <OrderCycleBillingTermsForm
                customer={customer || undefined}
                isDisabled={orderInputs.length > 0}
                isChecked={isCycleBilled}
                isEditing={isEditingCycleBillingTerms}
                cycleBillingTerms={cycleBillingTerms}
                setIsChecked={setIsCycleBilled}
                setIsEditing={setIsEditingCycleBillingTerms}
                setCycleBillingTerms={setCycleBillingTerms}
              />

              {configs['CONF_AUTO_CHARGE_ORDER']?.value === '1' && (
                <div className="flex flex-col gap-[12px] mt-3">
                  <div className="w-fit">
                    <Tooltip
                      tooltip="Use a card payment method to enable automatic payments"
                      invisible={[OrderPaymentMethod.CreditCard, OrderPaymentMethod.Terminal].includes(
                        paymentType ?? OrderPaymentMethod.ProceedWithoutPayment,
                      )}
                      type="black"
                    >
                      <Checkbox
                        type="white"
                        isDisabled={
                          !isCycleBilled ||
                          ![
                            OrderPaymentMethod.CreditCard,
                            OrderPaymentMethod.Terminal,
                            OrderPaymentMethod.BankAccount,
                          ].includes(paymentType ?? OrderPaymentMethod.ProceedWithoutPayment)
                        }
                        isChecked={
                          isAutoCharged &&
                          [
                            OrderPaymentMethod.CreditCard,
                            OrderPaymentMethod.Terminal,
                            OrderPaymentMethod.BankAccount,
                          ].includes(paymentType ?? OrderPaymentMethod.ProceedWithoutPayment)
                        }
                        onChange={() =>
                          setIsAutoCharged(
                            !isAutoCharged &&
                              [
                                OrderPaymentMethod.CreditCard,
                                OrderPaymentMethod.Terminal,
                                OrderPaymentMethod.BankAccount,
                              ].includes(paymentType ?? OrderPaymentMethod.ProceedWithoutPayment),
                          )
                        }
                        label={
                          <Typography type="text-md" className="text-grey-700">
                            Automatic card payments
                          </Typography>
                        }
                        dataCy="auto-charge"
                      />
                    </Tooltip>
                  </div>

                  <Typography type="text-sm" className="text-grey-500">
                    Invoices paid by card will automatically be charged on the invoice due date up to the total amount
                    owed.{' '}
                  </Typography>
                </div>
              )}
            </OrderSection>
          )}

          {/* Add Products */}
          <AddProduct
            locationId={location?.id}
            userId={customer?.id}
            setOrderInputs={setOrderInputs}
            isCycleBilled={isCycleBilled}
            cycleLength={cycleBillingTerms.order.cycleLength}
            isCashCustomer={customer?.type === UserType.Cash}
          />

          {/* Products details */}
          {summary && summary.lineItems.length > 0 && (
            <TableProducts
              location={location!}
              summary={summary}
              orderInputItems={orderInputs}
              selectedUnits={selectedUnits}
              setOrderInputs={setOrderInputs}
              setSelectedUnits={setSelectedUnits}
              setCustomProductFees={setCustomProductFees}
              isCycleBilled={isCycleBilled}
              saleSerialTrackingEnabled={configs['CONF_SALE_SERIAL_TRACKING']?.value === '1'}
              deliveryDescriptions={deliveryDescriptions}
              documentType={isCreditMemo ? OrderDocumentType.CreditMemo : OrderDocumentType.Order}
            />
          )}

          {/* Order Notes and Totals */}
          <div className="flex flex-col-reverse md:flex-row gap-[15px]">
            {/* Notes */}
            <div className="flex flex-col gap-[15px] w-full">
              <OrderSection header="Delivery" className="flex flex-col gap-3">
                <OrderDeliverySelection
                  locationId={location?.id}
                  productTypes={orderInputs.map((input) => ({ id: input.productId, type: input.type }))}
                  selected={selectedDelivery}
                  setSelected={setSelectedDelivery}
                  setDeliveryDescriptions={setDeliveryDescriptions}
                />
              </OrderSection>
              <OrderSection className="flex flex-col gap-[12px] grow" header="Order notes">
                <TextArea
                  classNameWrapper="h-full"
                  className="w-full h-full resize-none bg-grey-50 text-sm"
                  autoResize={false}
                  placeholder="Add a note"
                  value={notes}
                  onChange={({ target }) => setNotes(target.value)}
                  dataCy="order-notes"
                />
              </OrderSection>
            </div>

            {/* Totals */}
            {!!location && !!additionalDiscount ? (
              <OrderTotals
                isEditable={true}
                header={'Order Summary'}
                summary={summary}
                coupon={coupon}
                tagPricing={customer?.tagPricing?.name}
                setCoupon={setCoupon}
                setCustomDelivery={setCustomDelivery}
                setCustomPickup={setCustomPickup}
                setCustomTax={setCustomTax}
                setCustomTaxPercentage={setCustomTaxPercentage}
                setCustomDamageWaiver={setCustomDamageWaiver}
                setCustomFees={setCustomFees}
                setAdditionalDiscount={setAdditionalDiscount}
                taxBreakdown={taxBreakdown}
                setTaxBreakdown={setTaxBreakdown}
                location={location}
                isDelivery={selectedDelivery ? selectedDelivery !== ShippingType.SelfPickUp : false}
                taxCodeOptions={
                  taxRates
                    ? { taxCodes: taxRates, setTaxCode: setTaxRate, taxCode: taxRate, defaultTaxCode }
                    : undefined
                }
                isTaxAmountEditable={!isCycleBilled}
              />
            ) : (
              <div></div>
            )}
          </div>

          {/* Payment */}
          {!isQuote && !isMissedRental && !isCreditMemo && location && (
            <OrderSection className="flex flex-col gap-[16px]" header="Payment">
              <PaymentContextProvider>
                <OrderPaymentMethodSelector
                  customer={customer}
                  paymentMethod={paymentType}
                  location={location}
                  setPaymentMethod={setPaymentType}
                  setCardId={setCardId}
                  card={card}
                  setCard={setCard}
                  setPoNumber={setPoNumber}
                  poNumber={poNumber}
                  setBankAccountId={setBankAccountId}
                  setCustomPaymentMethod={setCustomPaymentMethod}
                  customPaymentMethod={customPaymentMethod}
                  showTerminal={location!.currency !== 'USD'}
                  setOnAccount={setOnAccount}
                />
              </PaymentContextProvider>
            </OrderSection>
          )}
        </div>

        <div className="order-1 lg:order-2">
          {!customer ? (
            <OrderSection className="flex flex-col gap-[12px]" header="Search Customer">
              <CustomerSelect
                setCustomer={(c) => {
                  setCustomer(c);
                  c?.tagPricing && setCoupon(c.tagPricing.name);
                }}
              />
            </OrderSection>
          ) : (
            <>
              <CustomerInfo
                customer={customer}
                setCustomer={setCustomer}
                billing={billingAddress}
                delivery={deliveryAddress}
                representative={representative}
                setBilling={setBillingAddress}
                setDelivery={setDeliveryAddress}
                setRepresentative={setRepresentative}
                setBalanceAmount={setBalance}
                isCreateMode
                isEditMode
              />
            </>
          )}
          {configs['CONF_COMMISSION_ENABLED']?.value === '1' && (
            <div className="mt-[12px]">
              <AssignSalesRep salesRep={salesRep} updateSalesRep={(rep?: number) => setSalesRep(rep)} />
            </div>
          )}
        </div>
      </div>
      {showApproveModal && orderId && (
        <ApproveOrder
          orderId={orderId}
          onCancel={() => navigate(`${contextPath}manager/orders/view/${orderId}`)}
          onComplete={() => navigate(`${contextPath}manager/orders/view/${orderId}`)}
          card={card}
        />
      )}
      {showCaptureModal && orderId && customer && (card || paymentType == OrderPaymentMethod.Terminal) && (
        <CapturePayment
          orderId={orderId}
          setCard={async () => {
            try {
              card && (await createOrderCard(customer!.id, card.confirmationTokenId!, orderId));
              await autoApprove(orderId);
            } catch (e: any) {
              //skip
            } finally {
              navigate(`${contextPath}manager/orders/view/${orderId}`);
            }
          }}
          onComplete={async () => {
            try {
              await autoApprove(orderId);
            } catch (e: any) {
              //skip
            } finally {
              navigate(`${contextPath}manager/orders/view/${orderId}`);
            }
          }}
          onCancel={() => navigate(`${contextPath}manager/orders/view/${orderId}`)}
          card={card}
          isTerminal={paymentType === OrderPaymentMethod.Terminal}
        ></CapturePayment>
      )}

      {hasPermissions(['Credit Limit Adjustment']) && (
        <ConfirmModal
          onConfirm={submit}
          close={() => {
            setTotalExceedsLimit(false);
          }}
          isOpen={totalExceedsLimit}
          prompt={'Credit Limit Exceeded'}
          additionalText={"Placing this order will go over the customer's credit limit. Would you like to continue?"}
        ></ConfirmModal>
      )}
    </div>
  );
};

const TableProducts = ({
  location,
  summary,
  orderInputItems,
  selectedUnits,
  setOrderInputs,
  setSelectedUnits,
  setCustomProductFees,
  isCycleBilled,
  saleSerialTrackingEnabled,
  deliveryDescriptions,
  documentType,
}: {
  location: Location;
  summary: FinancialSummary | undefined;
  orderInputItems: OrderInputItem[];
  selectedUnits: SelectedUnits[];
  setOrderInputs: Dispatch<SetStateAction<OrderInputItem[]>>;
  setSelectedUnits: Dispatch<SetStateAction<SelectedUnits[]>>;
  setCustomProductFees: Dispatch<SetStateAction<CustomProductFeeRequest[]>>;
  isCycleBilled: boolean;
  saleSerialTrackingEnabled: boolean;
  deliveryDescriptions: DeliveryDescription[];
  documentType: OrderDocumentType;
}) => {
  const { confirmAvailability, getConfiguration } = useAPI();
  // States
  const [data, setData] = useState<DataTableRow[]>([]);
  // Vars
  const columns: DataTableColumn[] = [
    { key: 'product', label: 'Product Name', className: '!py-[13px] !pl-[24px]' },
    { key: 'discount', label: 'Disc. (%)', className: '!py-[13px]' },
    { key: 'qty', label: 'Qty', className: '!py-[13px]' },
    { key: 'units', label: 'Assign Unit', className: '!py-[13px] max-w-[200px]' },
    { key: 'total', label: 'Total', className: '!py-[13px] text-right' },
    { key: 'delete', label: '', className: '!py-[13px]' },
  ];

  const removeOrderInput = (id: string) => {
    setOrderInputs(orderInputItems.filter((line) => line.id !== id));
  };

  const updateOrderInput = async (orderInput: OrderInputItem) => {
    try {
      !isOrderCustomInput(orderInput) && (await confirmAvailability({ ...orderInput, locationId: location.id }));
      setOrderInputs(orderInputItems.map((line) => (line.id === orderInput.id ? orderInput : line)));
    } catch (error: any) {
      toast(error.message, 'error');
    }
  };

  const [productImagesV2, setProductImagesV2] = useState<boolean>(true);
  useEffect(() => {
    getConfiguration('CONF_PRODUCT_IMAGES_V2').then((config) => {
      setProductImagesV2(config?.value === '1');
    });
  }, []);

  useEffect(() => {
    const productLineItems = (summary?.lineItems.filter((line) => line.type === 'Product') || []) as ProductLineItem[];
    const customLineItems = (summary?.lineItems.filter((line) => line.type === 'CustomProduct') ||
      []) as CustomProductLineItem[];
    const data = orderInputItems.flatMap((input) => {
      const lineItem = productLineItems.find((line) => line.id === input.id);
      if (lineItem && !isOrderCustomInput(input)) {
        const productFees = summary?.lineItems.filter(
          (line) => line.type === 'ProductFee' && line.productLineItemId === input.id,
        ) as ProductFeeLineItem[];
        const securityDeposit = summary?.lineItems.find(
          (li) => li.type === 'ProductSecurityDeposit' && li.productLineItemId === input.id,
        ) as ProductSecurityDepositLineItem | undefined;
        return [
          {
            fields: {
              product: (
                <ProductCell
                  input={input}
                  lineItem={lineItem}
                  productFees={productFees}
                  securityDeposit={securityDeposit}
                  onChange={updateOrderInput}
                  setCustomProductFees={setCustomProductFees}
                  productImagesV2={productImagesV2}
                  isCycleBilled={isCycleBilled}
                />
              ),
              discount: <DiscountCell input={input} onChange={updateOrderInput} />,
              qty: <QtyCell input={input} onChange={updateOrderInput} />,
              units: (
                <AssignUnitCell
                  input={input}
                  lineItem={lineItem}
                  locationId={location.id}
                  selectedUnits={selectedUnits}
                  setSelectedUnits={setSelectedUnits}
                  saleSerialTrackingEnabled={saleSerialTrackingEnabled}
                  documentType={documentType}
                />
              ),
              total: (
                <Typography type="text-sm" className="text-quipli-black-800 text-right">
                  {lineItem.amount.toFormat()}
                </Typography>
              ),
              delete: <DeleteCell id={input.id} onDelete={removeOrderInput} />,
            },
            mobileHeader: <Typography type="text-sm">Mobile</Typography>,
            extraRow: {
              content: (
                <DeliveryAndDatesCell
                  input={input}
                  location={location}
                  onChange={updateOrderInput}
                  deliveryDescription={deliveryDescriptions.find(({ productId }) => productId === input.productId)}
                />
              ),
              className: '!px-[24px] !py-[16px]',
            },
            key: input.id,
          },
        ];
      }

      const customLineItem = customLineItems.find((line) => line.id === input.id);
      if (customLineItem) {
        return [
          {
            fields: {
              product: <CustomProductCell input={input} lineItem={customLineItem} onChange={updateOrderInput} />,
              discount: (
                <Typography type="text-sm" className="text-grey-500">
                  N/A
                </Typography>
              ),
              qty: <QtyCell input={input} onChange={updateOrderInput} />,
              units: (
                <Typography type="text-sm" className="text-grey-500">
                  N/A
                </Typography>
              ),
              total: (
                <Typography type="text-sm" className="text-quipli-black-800 text-right">
                  {customLineItem.amount.toFormat()}
                </Typography>
              ),
              delete: <DeleteCell id={input.id} onDelete={removeOrderInput} />,
            },
            mobileHeader: <Typography type="text-sm">Mobile</Typography>,
            extraRow:
              input.type === OrderType.Rental
                ? {
                    content: (
                      <DeliveryAndDatesCell
                        input={input}
                        location={location}
                        onChange={updateOrderInput}
                        deliveryDescription={deliveryDescriptions.find(
                          ({ productId }) => productId === input.productId,
                        )}
                      />
                    ),
                    className: '!px-[24px] !py-[16px]',
                  }
                : {
                    content: <></>,
                    className: '',
                  },
            key: input.id,
          },
        ];
      }

      return [];
    });
    if (data.length === orderInputItems.length) {
      setData(data);
    }
  }, [summary, selectedUnits, deliveryDescriptions]);

  return (
    <OrderSection className="!px-0 !py-[6px]">
      <DataTable columnNames={columns} data={data} showAlwaysDesktop={true} />
    </OrderSection>
  );
};

const ProductCell = ({
  input,
  lineItem,
  productFees,
  securityDeposit,
  onChange,
  setCustomProductFees,
  productImagesV2,
  isCycleBilled,
}: {
  input: OrderInputItem;
  lineItem: ProductLineItem;
  productFees: ProductFeeLineItem[];
  securityDeposit?: ProductSecurityDepositLineItem;
  onChange: (orderInput: OrderInputItem) => void;
  setCustomProductFees: Dispatch<SetStateAction<CustomProductFeeRequest[]>>;
  productImagesV2: boolean;
  isCycleBilled: boolean;
}) => {
  return (
    <div className="flex gap-[12px] max-w-[281px]">
      <div className="flex items-center">
        {productImagesV2 ? (
          <ProductImageV2
            productId={lineItem.productId}
            profile="MINI"
            className="min-h-[48px] min-w-[48px] max-h-[48px] max-w-[48px] aspect-square  rounded-[6.85px] border-[0.87px] border-solid border-grey-200 overflow-hidden "
          />
        ) : (
          <ProductImage
            productId={lineItem.productId}
            profile="MINI"
            className="min-h-[48px] min-w-[48px] max-h-[48px] max-w-[48px] aspect-square  rounded-[6.85px] border-[0.87px] border-solid border-grey-200 overflow-hidden "
          />
        )}
      </div>

      <div className="whitespace-pre-wrap">
        <Typography type="text-sm" className="text-quipli-black-800">
          {lineItem.description}
        </Typography>

        {isCycleBilled && lineItem.transactionType === OrderType.Rental ? (
          <div>
            <Typography type="text-sm" className="text-grey-500">
              {lineItem.unitPrice.toFormat()}
            </Typography>
          </div>
        ) : (
          <EditableField
            type="number"
            label="Price"
            formattedValue={lineItem.unitPrice.toFormat()}
            value={lineItem.unitPrice.toUnit()}
            onChange={(newPrice) => {
              onChange({ ...input, customPrice: safeRound(newPrice * 100) });
              toast('Price updated.', 'success');
            }}
            onReset={() => {
              onChange({ ...input, customPrice: undefined });
              toast('Price updated to default.', 'success');
            }}
            canReset={lineItem.hasCustomAmount}
            resetPosition="right"
            dataCy="product-price-input"
          />
        )}

        <div className="flex flex-col gap-[3px] mt-[10px] w-full">
          {productFees.map((fee) => (
            <div className="flex flex-col w-full">
              <Typography type="text-sm" className="text-grey-500">
                {fee.description}
              </Typography>

              <EditableField
                type="number"
                label={fee.description}
                formattedValue={fee.amount.toFormat()}
                value={fee.amount.toUnit()}
                onChange={(newPrice) => {
                  setCustomProductFees((prev) => [
                    ...prev.filter(
                      (custom) =>
                        !(custom.feeId === fee.productFeeId && custom.productLineItemId === fee.productLineItemId),
                    ),
                    {
                      feeId: fee.productFeeId,
                      productId: fee.productId,
                      productLineItemId: fee.productLineItemId,
                      amount: safeRound(newPrice * 100),
                    } as CustomProductFeeRequest,
                  ]);
                  toast(`${fee.description} updated.`, 'success');
                }}
                onReset={() => {
                  setCustomProductFees((prev) =>
                    prev.filter(
                      (custom) =>
                        !(custom.feeId === fee.productFeeId && custom.productLineItemId === fee.productLineItemId),
                    ),
                  );
                  toast(`${fee.description} updated to default.`, 'success');
                }}
                canReset={fee.hasCustomAmount}
                resetPosition="right"
              />
            </div>
          ))}

          {securityDeposit && (
            <div className="flex flex-col w-full">
              <Typography type="text-sm" className="text-grey-500">
                {securityDeposit.description}
              </Typography>

              <div>
                <Typography type="text-sm" className="text-grey-500">
                  {securityDeposit.amount.toFormat()}
                </Typography>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CustomProductCell = ({
  input,
  lineItem,
  onChange,
}: {
  input: OrderInputItem;
  lineItem: CustomProductLineItem;
  onChange: (orderInput: OrderInputItem) => void;
}) => {
  return (
    <div className="flex gap-[12px] max-w-[281px]">
      <div className="flex items-center">
        <ProductImageV2
          productId={'placeholder'}
          profile="MINI"
          className="min-h-[48px] min-w-[48px] max-h-[48px] max-w-[48px] aspect-square  rounded-[6.85px] border-[0.87px] border-solid border-grey-200 overflow-hidden "
        />
      </div>

      <div className="whitespace-pre-wrap">
        <Typography type="text-sm" className="text-quipli-black-800">
          {lineItem.description}
        </Typography>

        <EditableField
          type="number"
          label="Price"
          formattedValue={lineItem.unitPrice.toFormat()}
          value={lineItem.unitPrice.toUnit()}
          onChange={(newPrice) => {
            onChange({ ...input, customPrice: safeRound(newPrice * 100) });
            toast('Price updated.', 'success');
          }}
          resetPosition="right"
          dataCy="custom-product-price-input"
        />
      </div>
    </div>
  );
};

const DiscountCell = ({
  input,
  onChange,
}: {
  input: OrderInputItem;
  onChange: (orderInput: OrderInputItem) => void;
}) => {
  const handleOnChange = (newDiscount: number) => {
    if (newDiscount < 0 || newDiscount > 100) {
      toast('Discount must be between 0% and 100%', 'error');
      return;
    }

    onChange({ ...input, manualDiscount: newDiscount });
  };

  return (
    <EditableField
      value={input.manualDiscount || 0}
      onChange={handleOnChange}
      maximumFractionDigits={2}
      type="number"
      label="Discount"
      formattedValue={`${(input.manualDiscount || 0).toFixed(2)}%`}
      noValueText="0.00%"
      placeholder="0.00%"
      canReset={(input.manualDiscount || 0) > 0}
      onReset={() => onChange({ ...input, manualDiscount: undefined })}
      resetPosition="right"
      dataCy="manual-discount-input"
    />
  );
};

const QtyCell = ({ input, onChange }: { input: OrderInputItem; onChange: (orderInput: OrderInputItem) => void }) => {
  return (
    <FieldCounter
      value={input.quantity}
      onChange={(newQty) => onChange({ ...input, quantity: newQty })}
      allowDecimals={input.type === OrderType.Sale}
      min={input.type === OrderType.Sale ? 0 : 1}
    />
  );
};

const AssignUnitCell = ({
  input,
  lineItem,
  locationId,
  selectedUnits,
  saleSerialTrackingEnabled,
  setSelectedUnits,
  documentType,
}: {
  input: OrderInputItem;
  lineItem: ProductLineItem;
  locationId: number;
  selectedUnits: SelectedUnits[];
  saleSerialTrackingEnabled: boolean;
  setSelectedUnits: Dispatch<SetStateAction<SelectedUnits[]>>;
  documentType: OrderDocumentType;
}) => {
  const { getProduct, getAvailableCount } = useAPI();

  const [trackingType, setTrackingType] = useState<ProductTrackingType>();
  const [qtyAvailable, setQtyAvailable] = useState<number | undefined>(0);
  const [stock, setStock] = useState<number | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showQtyWarning, setShowQtyWarning] = useState<boolean>(false);
  const prev = usePrevious(input);

  const checkAvailableQuantity = async () => {
    const maxEndDate = input.type === OrderType.Rental ? input.start.plus({ days: 28 }) : undefined;
    const available = await getAvailableCount(
      input.productId,
      locationId,
      input.type,
      input.type === OrderType.Rental ? input.start : DateTime.now(),
      input.type === OrderType.Rental ? input.end ?? maxEndDate! : DateTime.now(),
    );
    setQtyAvailable(available);
    setShowQtyWarning(input.quantity > available);

    if (input.quantity > available) {
      toast(`Warning: You are exceeding the available quantity of ${lineItem.description}.`, 'warning');
    }
  };

  useEffect(() => {
    const get = async () => {
      try {
        setIsLoading(true);
        const prod = await getProduct(input.productId);
        setTrackingType(prod.trackingType);

        if (prod.trackingType === ProductTrackingType.Quantity) {
          setStock(
            input.type === OrderType.Rental
              ? prod.stock?.find((s) => s.locationId === locationId)?.rentalStock || prod.rentalStock
              : prod.stock?.find((s) => s.locationId === locationId)?.saleStock || prod.saleStock,
          );
          await checkAvailableQuantity();
        }
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };

    get();
  }, []);

  useEffect(() => {
    if (input.type !== OrderType.Rental || trackingType !== ProductTrackingType.Quantity) return;

    const prevInput = prev as OrderInputRental;
    if (input.start === prevInput.start && input.end === prevInput.end && input.quantity === prevInput.quantity) return;

    checkAvailableQuantity();
  }, [trackingType, input]);

  return trackingType !== ProductTrackingType.NonInventory &&
    (input.type === OrderType.Rental || saleSerialTrackingEnabled) ? (
    isLoading ? (
      <Spinner />
    ) : trackingType === ProductTrackingType.SerialUnits ? (
      <AssignUnitSelect
        locationId={locationId}
        uniqueId={input.id}
        productId={input.productId}
        quantity={input.quantity}
        startDate={input.type === OrderType.Rental ? input.start : DateTime.now()}
        endDate={input.type === OrderType.Rental ? input.end : DateTime.now()}
        selectedUnitIds={selectedUnits.find((u) => u.productLineItemId === input.id)?.units || []}
        onChange={(units) =>
          setSelectedUnits((prev) => [
            ...prev.filter((u) => u.productLineItemId !== input.id),
            { productId: input.productId, productLineItemId: input.id, units },
          ])
        }
        orderProductType={input.type}
        disabled={documentType == OrderDocumentType.CreditMemo}
      />
    ) : (
      <div className="flex flex-col items-center">
        <Typography type="text-sm" className="text-quipli-black-800" dataCy="assign-unit-qty-available">
          {`${qtyAvailable && Number(qtyAvailable).toFixed(2)} ${
            stock && `of ${Number(stock).toFixed(2)}`
          } units available`}
        </Typography>

        {showQtyWarning && <Badge type="warning" text="Exceeding available quantity" dataCy="assign-unit-exceeded" />}
      </div>
    )
  ) : (
    <Typography type="text-sm" className="text-grey-500" dataCy="assign-unit-na">
      N/A
    </Typography>
  );
};

const DeleteCell = ({ id, onDelete }: { id: string; onDelete: (id: string) => void }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  return (
    <>
      <Button className="button-tertiary !py-0" onClick={() => setIsOpen(true)} dataCy="delete-cell-button">
        <Icon icon="Trash" stroke="var(--grey-500)" />
      </Button>

      <ConfirmModal
        prompt="Remove item?"
        additionalText="This cannot be undone"
        noLabel="Cancel"
        yesLabel="Remove"
        yesDataCy="delete-cell-confirm"
        yesButtonYellow
        isOpen={isOpen}
        headerIcon={
          <div className="flex justify-center">
            <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
          </div>
        }
        onConfirm={() => {
          onDelete(id);
          toast('Product removed.', 'success');
          setIsOpen(false);
        }}
        close={() => setIsOpen(false)}
      />
    </>
  );
};

const DeliveryAndDatesCell = ({
  input,
  location,
  onChange,
  deliveryDescription,
}: {
  input: OrderInputItem;
  location: Location;
  onChange: (orderInput: OrderInputItem) => void;
  deliveryDescription?: DeliveryDescription;
}) => {
  const duration = input.type === OrderType.Rental && input.end ? input.end.diff(input.start) : undefined;
  const [newStartDate, setNewStartDate] = useState<DateTime | undefined>(
    input.type === OrderType.Rental ? input.start : undefined,
  );
  const [newEndDate, setNewEndDate] = useState<DateTime | undefined>(
    input.type === OrderType.Rental && input.end ? input.end : undefined,
  );

  useEffect(() => {
    onChange({
      ...input,
      ...(input.type === OrderType.Rental && { start: newStartDate!, end: newEndDate! }),
    });
  }, [newStartDate, newEndDate]);

  return (
    <div className="flex w-full gap-x-[26px]">
      <div className="basis-1/4 min-w-[25%] flex flex-col gap-1 flex-wrap">
        <Typography type="text-sm" weight="medium" className="text-quipli-black-800">
          Delivery:
        </Typography>

        <Typography type="text-sm" className="text-grey-500 whitespace-pre-wrap" dataCy="delivery-description">
          {deliveryDescription?.text || '-'}
        </Typography>
      </div>

      {input.type === OrderType.Rental && (
        <>
          <div className="basis-1/4 flex flex-col">
            <div className="flex items-center gap-x-[2px]">
              <Typography type="text-sm" weight="medium" className="text-sm text-quipli-black-800 items-center flex">
                From:
              </Typography>
              <Icon icon="Calendar" size="small" />
            </div>

            <div className="flex items-center min-h-[38px]">
              <DateTimePicker
                value={newStartDate}
                onChange={setNewStartDate}
                timezone={location?.timezone}
                usePortal={true}
                customDisplay={
                  <Typography type="text-sm" className="text-grey-500">
                    {newStartDate?.toFormat('LLL-dd-yyyy hh:mm a')}
                  </Typography>
                }
                dataCy="rental-start-date"
              />
            </div>
          </div>

          <div className="basis-1/4 flex flex-col">
            <div className="flex items-center gap-x-[2px]">
              <Typography type="text-sm" weight="medium" className="text-sm text-quipli-black-800 items-center flex">
                To:
              </Typography>
              <Icon icon="Calendar" size="small" />
            </div>

            <div className="flex items-center min-h-[38px]">
              <DateTimePicker
                value={newEndDate}
                onChange={setNewEndDate}
                timezone={location?.timezone}
                disabledIntervals={[
                  Interval.before(newStartDate?.plus({ minutes: 30 }) || DateTime.now(), { years: 10 }),
                ]}
                usePortal={true}
                customDisplay={
                  <Typography type="text-sm" className="text-grey-500">
                    {newEndDate?.toFormat('LLL-dd-yyyy hh:mm a') || 'Not set'}
                  </Typography>
                }
                dataCy="rental-end-date"
              />
            </div>
          </div>

          {duration && (
            <div className="basis-1/4 flex flex-col">
              <Typography type="text-sm" weight="medium" className="text-sm text-quipli-black-800">
                Duration:
              </Typography>

              <div className="flex items-center min-h-[38px]">
                <Typography type="text-sm" className="text-grey-500" dataCy="rental-duration">
                  {displayHumanDuration(duration)}
                </Typography>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
